import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { ShoppingBag, Filter } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import AddToCartModal from "@/components/AddToCartModal";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCart } from "@/contexts/CartContext";
import { resolveProductOptions } from "@/lib/productOptions";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const categories = [
  { key: "all", cz: "Vše", en: "All" },
  { key: "tshirt", cz: "Trička", en: "T-Shirts" },
  { key: "accessories", cz: "Doplňky", en: "Accessories" },
];

export default function MerchPage() {
  const { t, lang } = useLanguage();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState("all");

  useEffect(() => {
    axios.get(`${API}/products`)
      .then(res => setProducts(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const filtered = category === "all" ? products : products.filter(p => p.category === category);

  const [modalProduct, setModalProduct] = useState(null);
  const needsModal = (p) => {
    const o = resolveProductOptions(p);
    return o.showSizes || o.showColors || o.showCustom;
  };

  const handleAdd = (product) => {
    if (needsModal(product)) {
      setModalProduct(product);
      return;
    }
    addItem(product);
  };

  const handleConfirm = ({ size, color, custom_options, quantity }) => {
    if (modalProduct) addItem(modalProduct, quantity || 1, size, color, custom_options);
  };

  return (
    <div style={{ background: "#050505", minHeight: "100vh" }}>
      <SEO
        title="Merch e-shop – Trička, batohy a vinyl"
        description="Oficiální merch kapely Rebelky. Trička, batohy, mikiny, deníky a CD. Kvalitní fanouškovský merch s rychlým doručením po celé ČR. Podpoř svoji oblíbenou dívčí kapelu!"
        keywords="Rebelky merch, Rebelky tričko, Rebelky batoh, kapela merch CZ, fan merchandise, kapela Rebelky shop, dívčí kapela tričko"
        url="/merch"
      />
      <Navbar />
      <div style={{ paddingTop: "5rem" }}>
        {/* Header */}
        <section style={{ padding: "4rem 1.5rem 2rem", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
          <div className="max-w-7xl mx-auto">
            <p className="section-label mb-3">{t.merch.subtitle}</p>
            <div className="flex items-end justify-between flex-wrap gap-4">
              <h1 style={{ fontFamily: "Unbounded, sans-serif", fontWeight: 900, fontSize: "clamp(2rem, 6vw, 4rem)", color: "#FFFFFF", textTransform: "uppercase", letterSpacing: "-0.03em" }}>
                {t.merch.title}
              </h1>
              <Link to="/kosik" data-testid="go-to-cart" className="btn-outline-rebel rounded-none flex items-center gap-2" style={{ fontSize: "0.65rem", padding: "0.6rem 1.5rem" }}>
                <ShoppingBag size={14} /> {t.nav.cart}
              </Link>
            </div>

            {/* Category filter */}
            <div className="flex gap-2 mt-6" data-testid="category-filter">
              {categories.map(cat => (
                <button
                  key={cat.key}
                  data-testid={`filter-${cat.key}`}
                  onClick={() => setCategory(cat.key)}
                  style={{
                    background: category === cat.key ? "#FF005A" : "transparent",
                    border: "1px solid " + (category === cat.key ? "#FF005A" : "rgba(255,255,255,0.1)"),
                    color: "white",
                    padding: "0.4rem 1rem",
                    fontFamily: "Unbounded, sans-serif",
                    fontSize: "0.6rem",
                    letterSpacing: "0.1em",
                    cursor: "pointer",
                    borderRadius: "4px",
                    transition: "all 0.2s",
                  }}
                >
                  {lang === "cz" ? cat.cz : cat.en}
                </button>
              ))}
            </div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-6 py-8">
          {loading ? (
            <p style={{ color: "#52525B", fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", letterSpacing: "0.2em" }}>
              {t.common.loading}
            </p>
          ) : filtered.length === 0 ? (
            <p style={{ color: "#52525B" }}>{t.common.noResults}</p>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {filtered.map((product, i) => (
                <motion.div
                  key={product._id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  data-testid={`product-card-${product._id}`}
                  className="product-card"
                  style={{ background: "#0A0A0A", border: "1px solid rgba(255,255,255,0.06)", borderRadius: "12px", overflow: "hidden" }}
                >
                  <Link to={`/merch/${product._id}`}>
                    <div style={{ aspectRatio: "1", overflow: "hidden", background: "#121212", position: "relative" }}>
                      <img
                        src={product.image_url}
                        alt={product.name}
                        loading="lazy"
                        decoding="async"
                        className="product-image w-full h-full"
                        style={{ objectFit: "cover" }}
                        onError={e => { e.target.src = "https://images.unsplash.com/photo-1603208636533-5b965ef2dc22?w=400&q=80"; }}
                      />
                      {product.is_featured && (
                        <div style={{ position: "absolute", top: "0.5rem", left: "0.5rem", background: "#FF005A", padding: "0.2rem 0.5rem", borderRadius: "2px" }}>
                          <span style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.5rem", fontWeight: 700, letterSpacing: "0.1em", color: "white" }}>
                            {t.merch.featured}
                          </span>
                        </div>
                      )}
                      {(product.stock ?? 0) <= 0 && (
                        <div data-testid={`stock-badge-${product._id}`} style={{ position: "absolute", top: "0.5rem", right: "0.5rem", background: "rgba(229,255,0,0.95)", padding: "0.2rem 0.5rem", borderRadius: "2px" }}>
                          <span style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.5rem", fontWeight: 700, letterSpacing: "0.1em", color: "#0A0A0A" }}>
                            VÝROBA NA OBJEDNÁVKU
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>
                  <div style={{ padding: "1rem" }}>
                    <Link to={`/merch/${product._id}`} style={{ textDecoration: "none" }}>
                      <p style={{ fontFamily: "Unbounded, sans-serif", fontSize: "0.7rem", fontWeight: 700, color: "#FFFFFF", textTransform: "uppercase", marginBottom: "0.4rem" }} className="line-clamp-2 hover:text-pink-400">
                        {product.name}
                      </p>
                    </Link>
                    {product.sizes && product.sizes.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {product.sizes.map(s => (
                          <span key={s} style={{ fontSize: "0.55rem", border: "1px solid rgba(255,255,255,0.1)", padding: "1px 5px", color: "#A1A1AA", borderRadius: "2px" }}>{s}</span>
                        ))}
                      </div>
                    )}
                    <p style={{ color: "#FF005A", fontFamily: "Unbounded, sans-serif", fontWeight: 800, fontSize: "1rem", marginBottom: "0.8rem" }}>
                      {product.price} Kč
                    </p>
                    <button
                      onClick={() => handleAdd(product)}
                      data-testid={`add-to-cart-${product._id}`}
                      className="rounded-none w-full"
                      style={{
                        background: "#FF005A",
                        color: "white",
                        border: "none",
                        padding: "0.5rem",
                        fontFamily: "Unbounded, sans-serif",
                        fontSize: "0.55rem",
                        letterSpacing: "0.1em",
                        cursor: "pointer",
                        transition: "background 0.3s",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "4px",
                      }}
                    >
                      <ShoppingBag size={10} />
                      {needsModal(product) ? (lang === "cz" ? "VYBRAT VELIKOST" : "PICK SIZE") : t.merch.addToCart}
                    </button>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </div>
      <AddToCartModal
        product={modalProduct}
        open={!!modalProduct}
        onClose={() => setModalProduct(null)}
        onConfirm={handleConfirm}
      />
      <Footer />
    </div>
  );
}
